import { useFieldAccessState } from "@/composables/FieldAccessState";
import { required } from "@vuelidate/validators";
import { mapOrganizationTypeToShort } from "@/utils/organizationUtils";

/**
 * Composable for managing field required validators.
 * @param {Array} requiredFields - List of fields with their model types to validate.
 * @returns {Object} An object containing the required validators.
 */
export function useFieldRequiredValidator(requiredFields = []) {
  const fieldAccess = useFieldAccessState();
  const { getFieldValidationMessage } = fieldAccess;

  const isFieldRequired = (fieldName, siblings) => {
    const field = requiredFields.find((field) => field.fieldName === fieldName);
    const access = fieldAccess.getFieldAccess(field.modelType, fieldName, siblings.organizationTypeDesc);
    return access.required;
  };

  const isFieldVisible = (fieldName, siblings) => {
    const field = requiredFields.find((field) => field.fieldName === fieldName);
    const access =  fieldAccess.getFieldAccess(field.modelType, fieldName, siblings.organizationTypeDesc);
    return access.visible;
  };

  /**
   * Creates a conditional required validator for a specific field.
   * @param {string} modelType - The type of the model.
   * @param {string} fieldName - The name of the field.
   * @returns {Function} A validator function.
   */
  const createConditionalRequired = (modelType, fieldName) => {
    return conditionalRequired(modelType, fieldName, (fieldAccess, modelType, fieldName, siblings) => {
      if (!siblings.organizationTypeDesc) {
        return true;
      }

      const orgTypeShort = mapOrganizationTypeToShort()[siblings.organizationTypeDesc];
      const access = fieldAccess.getFieldAccess(modelType, fieldName, orgTypeShort);
      return access.required;
    });
  };

  /**
   * Creates a conditional validator based on a callback condition.
   * @param {string} modelType - The type of the model.
   * @param {string} fieldName - The name of the field.
   * @param {Function} conditionCallback - A callback to determine if the field is required.
   * @returns {Function} A validator function.
   */
  const conditionalRequired = function (modelType, fieldName, conditionCallback) {
    return function (value, siblings) {
      const isRequired = conditionCallback(fieldAccess, modelType, fieldName, siblings);
      return isRequired ? required.$validator(value) : true;
    };
  };

  /**
   * Generates required validators for all specified fields.
   * @returns {Object} An object mapping field names to their validator functions.
   */
  const requiredValidators = function () {
    const data = requiredFields.reduce((validators, { fieldName, modelType }) => {
      validators[fieldName] = createConditionalRequired(modelType, fieldName);
      return validators;
    }, {});

    return data;
  };


  return {
    requiredValidators,
    isFieldRequired,
    isFieldVisible,
    getFieldValidationMessage
  };
}